import React, { useCallback, useEffect } from "react";
import { IonDatetime, IonPopover } from "@ionic/react";
import { addYears, isSameDay, isValid, subYears } from "date-fns";
import _ from "lodash";
import { useFilePicker } from "use-file-picker";
import type { DatetimeChangeEventDetail } from "@ionic/core";
import {
  ProgramData,
  ICertificateInfo,
  IUserProgramCertificate,
  OnboardingCertificateIssueDate,
  IDatePickerType,
  ProgramCertificate,
  IUser,
} from "@interfaces/Interfaces";
import { Loadable } from "recoil";
import CertificatesList from "@components/programs/CertificatesList";
import { CERTIFICATE_ACCEPTED_FILE_TYPES, CERTIFICATE_MAX_FILE_SIZE } from "../../Constants";

interface CertificateManagementSectionProps {
  program: ProgramData;
  // State props
  allCertificates: ProgramCertificate[];
  uploads: ICertificateInfo[];
  deletions: ICertificateInfo[];
  existingCertificates: IUserProgramCertificate[];
  issueDates: OnboardingCertificateIssueDate[];
  expiryDates: OnboardingCertificateIssueDate[];
  certificateType: string;
  selectingCertificate: boolean;
  deletingCertificate: boolean;
  datePickerVisible: boolean;
  datePickerType: string;
  datePickerValue: Date;
  newDateInfo: (OnboardingCertificateIssueDate & { isIssueOrExpiry: string }) | null;
  changingDate: boolean;
  user: Loadable<IUser | null>;
  // State setters
  setAllCertificates: (certs: ProgramCertificate[]) => void;
  setUploads: (uploads: ICertificateInfo[]) => void;
  setDeletions: (deletions: ICertificateInfo[]) => void;
  setExistingCertificates: (certs: IUserProgramCertificate[]) => void;
  setIssueDates: (dates: OnboardingCertificateIssueDate[]) => void;
  setExpiryDates: (dates: OnboardingCertificateIssueDate[]) => void;
  setCertificateType: (type: string) => void;
  setSelectingCertificate: (selecting: boolean) => void;
  setDeletingCertificate: (deleting: boolean) => void;
  setDatePickerVisible: (visible: boolean) => void;
  setDatePickerType: (type: string) => void;
  setDatePickerValue: (value: Date) => void;
  setNewDateInfo: (info: (OnboardingCertificateIssueDate & { isIssueOrExpiry: string }) | null) => void;
  setChangingDate: (changing: boolean) => void;
}

const CertificateManagementSection: React.FC<CertificateManagementSectionProps> = ({
  program,
  allCertificates,
  uploads,
  deletions,
  existingCertificates,
  issueDates,
  expiryDates,
  certificateType,
  // selectingCertificate,
  deletingCertificate,
  datePickerVisible,
  datePickerType,
  datePickerValue,
  newDateInfo,
  changingDate,
  user,
  setAllCertificates,
  setUploads,
  setDeletions,
  setExistingCertificates,
  setIssueDates,
  setExpiryDates,
  setCertificateType,
  setSelectingCertificate,
  // setDeletingCertificate,
  setDatePickerVisible,
  setDatePickerType,
  setDatePickerValue,
  setNewDateInfo,
  setChangingDate,
}) => {
  // File picker setup
  const [openFileSelector, { filesContent, clear, plainFiles }] = useFilePicker({
    accept: CERTIFICATE_ACCEPTED_FILE_TYPES,
    maxFileSize: CERTIFICATE_MAX_FILE_SIZE,
    readAs: "DataURL",
    multiple: false,
    limitFilesConfig: { max: 1 },
  });

  // Load program certificates
  useEffect(() => {
    if (program?.Certificates) {
      setAllCertificates(_.orderBy(program.Certificates, ["Required", "Name"], ["asc", "asc"]));
    }
  }, [program?.Certificates, setAllCertificates]);

  // Handle file picker results
  useEffect(() => {
    if (plainFiles.length > 0 && filesContent.length > 0) {
      const _certificate: ICertificateInfo = {
        name: plainFiles[0].name,
        type: plainFiles[0].type,
        size: plainFiles[0].size,
        content: filesContent[0].content,
        lastModified: plainFiles[0].lastModified,
        certificateType: certificateType,
      };

      const array = _.cloneDeep(uploads);

      if (array.length === 0) {
        array.push(_certificate);
      } else {
        const index = _.findIndex(array, (item: ICertificateInfo) => item.certificateType === certificateType);

        if (index > -1) {
          array.splice(index, 1);
          array.push(_certificate);
        } else {
          array.push(_certificate);
        }
      }

      setUploads(array);
      clear();
    }
  }, [certificateType, clear, filesContent, plainFiles, uploads, setUploads]);

  // Load existing user certificates
  useEffect(() => {
    const getUserProgramCertificates = (): void => {
      if (user.state === "hasValue" && user.contents) {
        let _programCertificates = user.contents.programCertificates || [];
        _programCertificates = _programCertificates.filter(
          (item: IUserProgramCertificate) => item.programData.programName === program?.Name
        );

        setExistingCertificates(_programCertificates);
      }
    };

    if (user.state === "hasValue" && program) {
      getUserProgramCertificates();
    }
  }, [user, program, setExistingCertificates]);

  // Handle date changes
  useEffect(() => {
    if (newDateInfo && changingDate) {
      try {
        if (newDateInfo.isIssueOrExpiry === IDatePickerType.ISSUE) {
          const _issueDates = _.cloneDeep(issueDates);
          const index = _issueDates.findIndex((item) => item.type === newDateInfo.type);

          const dateInfo: OnboardingCertificateIssueDate = {
            date: newDateInfo.date,
            type: newDateInfo.type,
          };

          if (index > -1) {
            _issueDates.splice(index, 1);
            _issueDates.push(dateInfo);
          } else {
            _issueDates.push(dateInfo);
          }

          setIssueDates(_issueDates);
        } else {
          const _expiryDates = _.cloneDeep(expiryDates);
          const index = _expiryDates.findIndex((item) => item.type === newDateInfo.type);

          const dateInfo: OnboardingCertificateIssueDate = {
            date: newDateInfo.date,
            type: newDateInfo.type,
          };

          if (index > -1) {
            _expiryDates.splice(index, 1);
            _expiryDates.push(dateInfo);
          } else {
            _expiryDates.push(dateInfo);
          }

          setExpiryDates(_expiryDates);
        }

        setDatePickerVisible(false);
        setChangingDate(false);
        setNewDateInfo(null);
      } catch (error) {
        console.log(error);
        window.alert("Failed to change issue date. Please try again");
      }
    }
  }, [
    allCertificates.length,
    changingDate,
    expiryDates,
    issueDates,
    newDateInfo,
    setChangingDate,
    setDatePickerVisible,
    setExpiryDates,
    setIssueDates,
    setNewDateInfo,
  ]);

  const addCertificate = async (type: string): Promise<void> => {
    try {
      setCertificateType(type);
      setSelectingCertificate(true);
      openFileSelector();
    } catch (error) {
      console.log(error);
      setSelectingCertificate(false);
      window.alert("Failed to add certificate. Please try again");
    }
  };

  const removeExistingCertificate = async (certificate: IUserProgramCertificate): Promise<void> => {
    const toDelete = _.cloneDeep(deletions);
    const _programCertificates = _.cloneDeep(existingCertificates);

    const index = toDelete.findIndex((item) => item.certificateType === certificate.certificate?.certificateType);
    const programsCertificateIndex = _programCertificates.findIndex((item) => {
      return item.certificate?.certificateType === certificate.certificate?.certificateType;
    });

    if (index > -1) {
      toDelete.splice(index, 1);
      toDelete.push(certificate.certificate!);
    } else {
      toDelete.push(certificate.certificate!);
    }

    if (programsCertificateIndex > -1) {
      _programCertificates.splice(programsCertificateIndex, 1);
    }

    setDeletions(toDelete);
    setExistingCertificates(_programCertificates);
  };

  const removeCertificateFromUpload = (type: string): void => {
    const toUpload = _.cloneDeep(uploads);
    const _issueDates = _.cloneDeep(issueDates);
    const _expiryDates = _.cloneDeep(expiryDates);

    const uploadsIndex = toUpload.findIndex((item) => item.certificateType === type);
    const issueDatesIndex = _issueDates.findIndex((item) => item.type === type);
    const expiryDatesIndex = _expiryDates.findIndex((item) => item.type === type);

    if (uploadsIndex > -1) {
      toUpload.splice(uploadsIndex, 1);
    }

    if (issueDatesIndex > -1) {
      _issueDates.splice(issueDatesIndex, 1);
    }

    if (expiryDatesIndex > -1) {
      _expiryDates.splice(expiryDatesIndex, 1);
    }

    setUploads(toUpload);
    setIssueDates(_issueDates);
    setExpiryDates(_expiryDates);
  };

  const openDatePicker = (pickerType: string, type: string, initialDate: Date): void => {
    setDatePickerVisible(true);
    setCertificateType(type);
    setDatePickerType(pickerType);

    if (initialDate && isValid(initialDate)) {
      setDatePickerValue(initialDate);
    } else {
      setDatePickerValue(new Date());
    }
  };

  const changeDate = useCallback(
    (
      event: CustomEvent<DatetimeChangeEventDetail>,
      pickerType: string,
      certificateType: string,
      initialDate: Date
    ): void => {
      try {
        const dateValue = typeof event.detail.value === "string" ? event.detail.value : "";
        const isSameDate = isSameDay(new Date(dateValue), initialDate);

        if (newDateInfo === null && !isSameDate && !changingDate && dateValue) {
          setChangingDate(true);
          setDatePickerValue(new Date(dateValue));
          setNewDateInfo({
            date: new Date(dateValue),
            isIssueOrExpiry: pickerType,
            type: certificateType,
          });
        }
      } catch (error) {
        console.log(error);
        window.alert("Failed to change issue date. Please try again");
      }
    },
    [changingDate, newDateInfo, setChangingDate, setDatePickerValue, setNewDateInfo]
  );

  const closeDatePicker = (): void => {
    setDatePickerVisible(false);
  };

  return (
    <>
      {/* Date Picker Popover */}
      <IonPopover
        isOpen={datePickerVisible}
        onDidDismiss={() => closeDatePicker()}
      >
        <IonDatetime
          mode="md"
          size="cover"
          value={datePickerValue.toISOString()}
          onIonChange={(event) => changeDate(event, datePickerType, certificateType, datePickerValue)}
          min={subYears(new Date(), 10).toISOString()}
          max={
            datePickerType === IDatePickerType.EXPIRY
              ? addYears(new Date(), 10).toISOString()
              : new Date().toISOString()
          }
          presentation="date"
          firstDayOfWeek={1}
        />
      </IonPopover>

      {/* Certificate List */}
      <CertificatesList
        program={program}
        certs={allCertificates}
        issueDates={issueDates}
        expiryDates={expiryDates}
        existingCertificates={existingCertificates}
        uploads={uploads}
        deletingCertificate={deletingCertificate}
        certificateType={certificateType}
        removeExistingCertificate={removeExistingCertificate}
        removeCertificateFromUpload={removeCertificateFromUpload}
        addCertificate={addCertificate}
        openDatePicker={openDatePicker}
      />
    </>
  );
};

export default CertificateManagementSection;
